import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemText,
  Tabs,
  Tab,
  Divider,
  Select,
  MenuItem,
  FormControl,
  Radio,
  FormControlLabel,
  LinearProgress,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import { Base_URL, ApiCall } from '../Common/Path';
import customFailAlert from '../Alerts/customFailAlert';
import customSuccessAlert from '../Alerts/customSuccessAlert';

const CreateHouseholdModal = ({ open, onClose, onCreateResult }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [householdName, setHouseholdName] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [emails, setEmails] = useState([]);
  const [searchedAccounts, setSearchedAccounts] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [activeAccountTab, setActiveAccountTab] = useState(0);
  const [inactiveEmails, setInactiveEmails] = useState([]);
  const [selectedAccountTypes, setSelectedAccountTypes] = useState({});
  const [invitedEmails, setInvitedEmails] = useState([]);
  const [invitingEmails, setInvitingEmails] = useState(new Set()); // Track emails being invited
  const [accountPermissions, setAccountPermissions] = useState({});
  const [ownerAccount, setOwnerAccount] = useState(null);
  const [permissionModalOpen, setPermissionModalOpen] = useState(false);
  const [currentAccountForPermission, setCurrentAccountForPermission] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [isLoadingPermissionGroups, setIsLoadingPermissionGroups] = useState(false);
  const [permissionGroupsError, setPermissionGroupsError] = useState(null);
  const [investorPermissionGroups, setInvestorPermissionGroups] = useState({
    viewOnly: null,
    viewTrade: null
  });
  // const [customPermissions, setCustomPermissions] = useState({
  //   createUser: false,
  //   updateUserInfo: false,
  //   viewReports: false,
  //   manageTransactions: false,
  //   editSettings: false,
  //   deleteUser: false
  // });
  const [isSearchingAccounts, setIsSearchingAccounts] = useState(false);
  const [isCreatingHousehold, setIsCreatingHousehold] = useState(false);

  const steps = [
    'Enter Household Name',
    'Enter Investor Emails',
    'Select Household Accounts',
    'Set Member Permissions'
  ];

  // Email validation
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Handle email input
  const handleEmailInputChange = (e) => {
    setEmailInput(e.target.value);
  };

  const handleEmailInputKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
      e.preventDefault();
      addEmail();
    }
  };

  const addEmail = () => {
    const trimmedEmail = emailInput.trim().replace(/,$/g, '');
    if (trimmedEmail && isValidEmail(trimmedEmail) && !emails.includes(trimmedEmail)) {
      setEmails([...emails, trimmedEmail]);
      setEmailInput('');
    }
  };

  const removeEmail = (emailToRemove) => {
    setEmails(emails.filter(email => email !== emailToRemove));
  };

  // Normalize API user role object into account object used by UI
  const mapUserRoleToAccount = (userRole, index) => {
    if (!userRole) return null;

    const user = userRole.user || {};
    const details = user.userDetailsDto || {};

    const firstName = details.firstName || user.firstName || '';
    const lastName = details.lastName || user.lastName || '';
    const fullName = [firstName, lastName].filter(Boolean).join(' ');

    const displayName =
      userRole.username ||
      userRole.name ||
      fullName ||
      user.email ||
      'Unknown Investor';

    const email = user.email || userRole.email || '';

    const rawType =
      userRole.investorType ||
      userRole.accountType ||
      userRole.type ||
      userRole.entity ||
      '';

    let typeLabel = 'Individual';
    const normalizedType = String(rawType || '').toUpperCase();
    if (normalizedType.includes('CORP')) typeLabel = 'Corporate';
    else if (normalizedType.includes('TRUST')) typeLabel = 'Trust';

    return {
      id: userRole.id || userRole.userRoleId || index + 1,
      name: displayName,
      email,
      type: typeLabel,
      active: true,
      _raw: userRole
    };
  };

  // Handle search accounts via API
  const handleSearchAccounts = async () => {
    if (emails.length === 0 || isSearchingAccounts) return;

    setIsSearchingAccounts(true);
    // Reset previous search state
    setSearchedAccounts([]);
    setSelectedAccounts([]);
    setInactiveEmails([]);
    setInvitedEmails([]);
    setSelectedAccountTypes({});
    setActiveAccountTab(0);

    try {
      const url = `${Base_URL}/households/user-roles/by-emails`;
      // Backend expects emails as an array of raw email strings
      const body = {
        emails
      };

      const response = await ApiCall(url, body, 'post');


      const {
        statusCode,
        status,
        code,
        message,
        data: responseData
      } = response || {};

      const isSuccess =
        statusCode === 200 ||
        statusCode === 201 ||
        code === 200 ||
        code === 201 ||
        String(status || '').toUpperCase() === 'SUCCESS';

      if (!isSuccess) {
        customFailAlert(
          message || 'Unable to fetch accounts for the provided emails.'
        );
        return;
      }

      // Extract user roles / accounts array from different possible shapes
      const data = responseData || response || {};

      let userRoles = [];

      if (Array.isArray(data)) {
        userRoles = data;
      } else if (Array.isArray(data.userRoles)) {
        userRoles = data.userRoles;
      } else if (Array.isArray(data.accounts)) {
        userRoles = data.accounts;
      } else if (Array.isArray(data.items)) {
        userRoles = data.items;
      }

      const mappedAccounts = userRoles
        .map((item, index) => mapUserRoleToAccount(item, index))
        .filter(Boolean)
        .filter((account) => {
          // Filter to only show INVESTOR roles, exclude SUB_INVESTOR roles
          const rawRole = account._raw;
          if (!rawRole) return true;
          
          // Check various possible role field names (including nested in user object)
          const roleType = 
            rawRole.role || 
            rawRole.roleType || 
            rawRole.groupRole || 
            rawRole.userType ||
            rawRole.user?.role ||
            rawRole.user?.roleType ||
            rawRole.user?.userType ||
            '';
          
          const normalizedRole = String(roleType).toUpperCase();
          
          // If role cannot be determined, include it (household user roles are typically INVESTOR)
          if (!normalizedRole) return true;
          
          // Exclude SUB_INVESTOR roles, only include INVESTOR roles
          return normalizedRole === 'INVESTOR';
        });

      setSearchedAccounts(mappedAccounts);

      // Prefer inactive email list from API if present
      let inactiveFromApi = [];
      if (Array.isArray(data.inactiveEmails)) {
        inactiveFromApi = data.inactiveEmails;
      } else if (Array.isArray(data.emailsNotFound)) {
        inactiveFromApi = data.emailsNotFound;
      }

      // Fallback: compute inactive emails from entered emails vs accounts found
      const emailsFound = new Set(
        mappedAccounts
          .map(acc => (acc.email || '').toLowerCase())
          .filter(Boolean)
      );

      const computedInactive = emails.filter(
        email => !emailsFound.has(email.toLowerCase())
      );

      const mergedInactive = Array.from(
        new Set([...(inactiveFromApi || []), ...computedInactive])
      );

      setInactiveEmails(mergedInactive);

      setActiveStep(2);
    } catch (error) {
      console.error('Error searching household accounts:', error);
      customFailAlert('Something went wrong while searching accounts.');
    } finally {
      setIsSearchingAccounts(false);
    }
  };

  // Map UI account type to API investorType
  const mapAccountTypeToInvestorType = (accountType) => {
    switch (accountType) {
      case 'Individual':
        return 'INDIVIDUAL';
      case 'Corporate':
        return 'CORPORATE';
      case 'Trust':
        return 'TRUST';
      default:
        return 'INDIVIDUAL';
    }
  };

  // Handle invite inactive email
  const handleInviteEmail = async (email) => {
    const accountType = selectedAccountTypes[email];
    if (!accountType || invitingEmails.has(email)) return;

    // Set loading state for this email
    setInvitingEmails(prev => new Set([...prev, email]));

    try {
      // Map account type to API format
      const investorType = mapAccountTypeToInvestorType(accountType);

      // Prepare the request body according to API documentation
      const requestBody = {
        email: email,
        role: 'INVESTOR',
        investorType: investorType,
        entityInfo: {
          name: email.split('@')[0], // Use email prefix as default name
          entityType: 'INVESTOR'
        },
        isHouseholdMember: false
      };

      // Make API call to create user
      const url = `${Base_URL}/users`;
      const response = await ApiCall(url, requestBody, 'post');

      // Check if the response is successful
      const statusCode = response?.statusCode || response?.code;
      const isSuccess = statusCode === 200 || statusCode === 201;

      if (isSuccess) {
        // Extract userRoleId from response.data.userRole.id
        const userRoleId = response?.data?.userRole?.id;
        const userRoleData = response?.data?.userRole || {};
        
        if (!userRoleId) {
          customFailAlert('Failed to get user role ID from response.');
          return;
        }

        // Create account object similar to active accounts
        // Store the userRole object in _raw so handleCreateHousehold can access raw.id
        const invitedAccount = {
          id: userRoleId,
          name: email.split('@')[0], // Use email prefix as name
          email: email,
          type: accountType,
          active: true,
          _raw: userRoleData, // This contains the userRole object with the id field
          isInvited: true // Flag to identify invited users
        };

        // Add to invited emails with success status and account data
        setInvitedEmails(prev => [
          ...prev,
          {
            email,
            accountType,
            status: 'success',
            account: invitedAccount,
            userRoleId: userRoleId,
            response: userRoleData
          }
        ]);

        // Automatically add to selectedAccounts so it can be used in household creation
        setSelectedAccounts(prev => {
          // Check if already exists
          const exists = prev.find(acc => acc.email === email);
          if (exists) return prev;
          return [...prev, invitedAccount];
        });

        // Show success message
        customSuccessAlert('User invited successfully!');

        // Clear the selected account type for this email
        const newTypes = { ...selectedAccountTypes };
        delete newTypes[email];
        setSelectedAccountTypes(newTypes);
      } else {
        // Handle error response
        const errorMessage = response?.message || 'Failed to invite user. Please try again.';
        customFailAlert(errorMessage);
      }
    } catch (error) {
      console.error('Error inviting user:', error);
      customFailAlert('Something went wrong while inviting user. Please try again.');
    } finally {
      // Remove loading state for this email
      setInvitingEmails(prev => {
        const newSet = new Set(prev);
        newSet.delete(email);
        return newSet;
      });
    }
  };

  // Handle account type change for inactive email
  const handleAccountTypeChange = (email, type) => {
    setSelectedAccountTypes({
      ...selectedAccountTypes,
      [email]: type
    });
  };

  // Handle open permission modal
  const handleOpenPermissionModal = (account) => {
    setCurrentAccountForPermission(account);
    setSelectedTemplate('');
    setPermissionModalOpen(true);
  };

  // Normalize group name for easier matching
  const normalizeGroupName = (name) =>
    String(name || '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');

  // Fetch investor permission groups when permission modal opens
  useEffect(() => {
    const fetchInvestorPermissionGroups = async () => {
      if (!permissionModalOpen) return;

      // Avoid refetching if we already have groups
      if (investorPermissionGroups.viewOnly || investorPermissionGroups.viewTrade) return;

      setIsLoadingPermissionGroups(true);
      setPermissionGroupsError(null);

      try {
        const url = `${Base_URL}/permissions/groups?groupRole=INVESTOR`;
        const response = await ApiCall(url, null, 'get');

        const groups = Array.isArray(response?.data) ? response.data : [];

        const viewOnlyGroup = groups.find((group) => {
          const normalized = normalizeGroupName(group.groupName);
          return normalized === 'viewonly' || normalized === 'readonly';
        });

        const viewTradeGroup = groups.find((group) => {
          const normalized = normalizeGroupName(group.groupName);
          return (
            normalized === 'viewtrade' ||
            normalized === 'viewandtrade' ||
            normalized === 'tradeaccess'
          );
        });

        setInvestorPermissionGroups({
          viewOnly: viewOnlyGroup || null,
          viewTrade: viewTradeGroup || null
        });
      } catch (error) {
        console.error('Error fetching investor permission groups:', error);
        setPermissionGroupsError('Unable to load permission templates.');
        setInvestorPermissionGroups({
          viewOnly: null,
          viewTrade: null
        });
      } finally {
        setIsLoadingPermissionGroups(false);
      }
    };

    fetchInvestorPermissionGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissionModalOpen]);

  // Handle close permission modal
  const handleClosePermissionModal = () => {
    setPermissionModalOpen(false);
    setCurrentAccountForPermission(null);
    setSelectedTemplate('');
    // setCustomPermissions({
    //   createUser: false,
    //   updateUserInfo: false,
    //   viewReports: false,
    //   manageTransactions: false,
    //   editSettings: false,
    //   deleteUser: false
    // });
  };

  // Handle template selection
  const handleTemplateChange = (template) => {
    // If corresponding group is not available, do not allow selecting that template
    if (template === 'view-only' && !investorPermissionGroups.viewOnly) return;
    if (template === 'view-trade' && !investorPermissionGroups.viewTrade) return;

    setSelectedTemplate(template);

    // Reset custom permissions when switching to custom template
    // if (template === 'custom') {
    //   setCustomPermissions({
    //     createUser: false,
    //     updateUserInfo: false,
    //     viewReports: false,
    //     manageTransactions: false,
    //     editSettings: false,
    //     deleteUser: false
    //   });
    // }
  };

  // Handle custom permission toggle
  // const handleCustomPermissionToggle = (permission) => {
  //   setCustomPermissions({
  //     ...customPermissions,
  //     [permission]: !customPermissions[permission]
  //   });
  // };

  // Handle continue from permission modal
  const handleContinuePermission = () => {
    if (currentAccountForPermission && selectedTemplate) {
      const permissionLabel =
        selectedTemplate === 'view-only'
          ? 'View Only'
          : selectedTemplate === 'view-trade'
          ? 'View & Trade'
          : 'Custom';

      let permissionsPayload;

      if (selectedTemplate === 'view-only') {
        permissionsPayload = {
          type: 'group',
          group: investorPermissionGroups.viewOnly || null
        };
      } else if (selectedTemplate === 'view-trade') {
        permissionsPayload = {
          type: 'group',
          group: investorPermissionGroups.viewTrade || null
        };
      } 
      // else {
      //   permissionsPayload = {
      //     type: 'custom',
      //     values: { ...customPermissions }
      //   };
      // }

      setAccountPermissions({
        ...accountPermissions,
        [currentAccountForPermission.id]: {
          template: selectedTemplate,
          label: permissionLabel,
          permissions: permissionsPayload
        }
      });

      handleClosePermissionModal();
    }
  };

  // Handle owner selection
  const handleOwnerChange = (accountId) => {
    setOwnerAccount(accountId);
  };

  // Handle add account
  const handleAddAccount = (account) => {
    if (!selectedAccounts.find(acc => acc.id === account.id)) {
      setSelectedAccounts([...selectedAccounts, account]);
    }
  };

  // Handle remove account
  const handleRemoveAccount = (accountId) => {
    setSelectedAccounts(selectedAccounts.filter(acc => acc.id !== accountId));
  };

  // Handle next step
  const handleNext = () => {
    if (activeStep === 0 && householdName.trim()) {
      setActiveStep(1);
    } else if (activeStep === 2) {
      // Allow proceeding if at least one account is selected (either from active accounts or invited users)
      if (selectedAccounts.length > 0) {
        setActiveStep(3);
      }
    }
  };

  // Handle back step
  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  // Handle close modal
  const handleCloseModal = () => {
    // Reset all states
    setActiveStep(0);
    setHouseholdName('');
    setEmailInput('');
    setEmails([]);
    setSearchedAccounts([]);
    setSelectedAccounts([]);
    setActiveAccountTab(0);
    setInactiveEmails([]);
    setSelectedAccountTypes({});
    setInvitedEmails([]);
    setInvitingEmails(new Set());
    setAccountPermissions({});
    setOwnerAccount(null);
    setPermissionModalOpen(false);
    setCurrentAccountForPermission(null);
    setSelectedTemplate('');
    // setCustomPermissions({
    //   createUser: false,
    //   updateUserInfo: false,
    //   viewReports: false,
    //   manageTransactions: false,
    //   editSettings: false,
    //   deleteUser: false
    // });
    onClose();
  };

  // Get step label color
  const getStepLabelColor = (index) => {
    if (index < activeStep) return '#2563EB';
    if (index === activeStep) return '#2563EB';
    return '#9CA3AF';
  };

  // Get type color
  const getTypeColor = (type) => {
    switch (type) {
      case 'Individual':
        return '#10B981';
      case 'Corporate':
        return '#8B5CF6';
      case 'Trust':
        return '#F59E0B';
      default:
        return '#6B7280';
    }
  };

  // Validation helpers for final submit
  const hasAllAccountPermissions =
    selectedAccounts.length > 0 &&
    selectedAccounts.every((account) => !!accountPermissions[account.id]);

  const hasOwnerSelected = !!ownerAccount;

  const canCreateHousehold = hasAllAccountPermissions && hasOwnerSelected;

  // Build and submit create household payload
  const handleCreateHousehold = async () => {
    if (!canCreateHousehold || isCreatingHousehold) return;

    try {
      setIsCreatingHousehold(true);

      const members = selectedAccounts.map((account) => {
        const raw = account._raw || {};

        const userRoleId =
          raw.userRoleId ||
          raw.id ||
          account.id;

        const permissionMeta = accountPermissions[account.id];
        const permissionsPayload = permissionMeta?.permissions || {};
        const groupForTemplate =
          permissionsPayload.type === 'group' ? permissionsPayload.group : null;

        return {
          userRoleId,
          permissionGroupId: groupForTemplate?.id || null,
          isOwner: ownerAccount === account.id
        };
      });

      const body = {
        householdName: householdName.trim(),
        description: '',
        members,
        invitationExpiryDays: 7
      };

      const url = `${Base_URL}/households`;
      const response = await ApiCall(url, body, 'post');

      const statusCode =
        response?.statusCode || response?.code;

      if (statusCode === 200 || statusCode === 201) {
        onCreateResult?.({
          success: true,
          message:
            response?.data?.message ||
            response?.message ||
            'Household created successfully'
        });
        handleCloseModal();
      } else {
        onCreateResult?.({
          success: false,
          message:
            response?.message || 'Unable to create household. Please try again.'
        });
      }
    } catch (error) {
      console.error('Error creating household:', error);
      onCreateResult?.({
        success: false,
        message: 'Something went wrong while creating household.'
      });
    } finally {
      setIsCreatingHousehold(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleCloseModal}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '4px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          maxHeight: '90vh',
          width: '700px'
        }
      }}
    >
      {/* Dialog Title */}
      <DialogTitle sx={{ pb: 2, pt: 3, px: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
              Create Household
            </Typography>
            <Typography variant="body2" sx={{ color: '#6B7280' }}>
              {activeStep === 0 && 'Complete the steps to create household'}
              {activeStep === 1 && 'Following accounts will be linked together in a household'}
              {activeStep === 2 && 'Following accounts will be linked together in a household'}
              {activeStep === 3 && 'Set permissions for household members'}
            </Typography>
          </Box>
          <IconButton onClick={handleCloseModal} sx={{ color: '#6B7280' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* Stepper */}
      <Box sx={{ px: 4, pt: 2, pb: 3, ml:7 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {steps.map((label, index) => (
            <Box key={label} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                {/* Circle */}
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    border: index === activeStep ? '3px solid #2563EB' : '2px solid #D1D5DB',
                    backgroundColor: index < activeStep ? '#2563EB' : '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    zIndex: 2
                  }}
                >
                  {index < activeStep ? (
                    <CheckIcon sx={{ color: '#fff', fontSize: 20 }} />
                  ) : (
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: index === activeStep ? '#2563EB' : '#D1D5DB'
                      }}
                    />
                  )}
                </Box>
                {/* Line */}
                {index < steps.length - 1 && (
                  <Box
                    sx={{
                      flex: 1,
                      height: 2,
                      backgroundColor: index < activeStep ? '#2563EB' : '#D1D5DB',
                      ml: -1,
                      mr: -1,
                      zIndex: 1
                    }}
                  />
                )}
              </Box>
              {/* Label */}
              <Typography
                variant="caption"
                sx={{
                  mt: 1,
                  ml: -9,
                  color: getStepLabelColor(index),
                  fontWeight: index === activeStep ? 600 : 500,
                  fontSize: '11px',
                  textAlign: 'center',
                  lineHeight: 1.2
                }}
              >
                {label} 
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Divider />

      {/* Dialog Content */}
      <DialogContent sx={{ px: 4, py: 4, minHeight: '300px' }}>
        {/* Step 1: Enter Household Name */}
        {activeStep === 0 && (
          <Box>
            <TextField
              fullWidth
              placeholder="Enter name of the household"
              value={householdName}
              onChange={(e) => setHouseholdName(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '4px',
                  '& fieldset': {
                    borderColor: '#D1D5DB',
                  },
                  '&:hover fieldset': {
                    borderColor: '#9CA3AF',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#2563EB',
                  }
                }
              }}
            />
          </Box>
        )}

        {/* Step 2: Enter Investor Emails */}
        {activeStep === 1 && (
          <Box>
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Enter one or more investors' emails to search accounts"
              value={emailInput}
              onChange={handleEmailInputChange}
              onKeyDown={handleEmailInputKeyDown}
              onBlur={addEmail}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '4px',
                  padding: 1.5,
                  '& fieldset': {
                    borderColor: '#2563EB',
                  },
                  '&:hover fieldset': {
                    borderColor: '#2563EB',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#2563EB',
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: emails.length > 0 ? 1 : 0 }}>
                    {emails.map((email, index) => (
                      <Chip
                        key={index}
                        label={email}
                        onDelete={() => removeEmail(email)}
                        sx={{
                          backgroundColor: '#F3F4F6',
                          borderRadius: '4px',
                          '& .MuiChip-deleteIcon': {
                            color: '#6B7280',
                            '&:hover': {
                              color: '#374151'
                            }
                          }
                        }}
                      />
                    ))}
                  </Box>
                )
              }}
            />
            <Typography variant="caption" sx={{ color: '#6B7280', mt: 1, display: 'block' }}>
              Press Enter, comma, or space to add multiple emails
            </Typography>
          </Box>
        )}

        {/* Step 3: Select Household Accounts */}
        {activeStep === 2 && (
          <Box>
            {/* Tabs */}
            <Tabs
              value={activeAccountTab}
              onChange={(e, newValue) => setActiveAccountTab(newValue)}
              sx={{
                mb: 3,
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#6B7280',
                  '&.Mui-selected': {
                    color: '#2563EB',
                    fontWeight: 600
                  }
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#2563EB'
                }
              }}
            >
              <Tab label="Active Accounts" />
              <Tab label="In-active emails" />
            </Tabs>

            {/* Account List */}
            {activeAccountTab === 0 && (
              <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
                {searchedAccounts.filter(acc => acc.active).map((account) => {
                  const isAdded = selectedAccounts.find(acc => acc.id === account.id);
                  
                  return (
                    <Box
                      key={account.id}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: 2,
                        borderBottom: '1px solid #E5E7EB',
                        '&:last-child': {
                          borderBottom: 'none'
                        }
                      }}
                    >
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#000' }}>
                            {account.name}
                          </Typography>
                          <Chip
                            label={account.type}
                            size="small"
                            sx={{
                              backgroundColor: `${getTypeColor(account.type)}20`,
                              color: getTypeColor(account.type),
                              fontWeight: 500,
                              fontSize: '11px',
                              height: '20px'
                            }}
                          />
                        </Box>
                        <Typography sx={{ fontSize: '13px', color: '#6B7280' }}>
                          {account.email}
                        </Typography>
                      </Box>
                      <Button
                        variant={isAdded ? 'outlined' : 'outlined'}
                        size="small"
                        onClick={() => isAdded ? handleRemoveAccount(account.id) : handleAddAccount(account)}
                        sx={{
                          textTransform: 'none',
                          borderColor: '#2563EB',
                          color: '#2563EB',
                          px: 3,
                          py: 0.75,
                          borderRadius: '4px',
                          fontWeight: 600,
                          '&:hover': {
                            backgroundColor: '#EFF6FF',
                            borderColor: '#2563EB'
                          }
                        }}
                      >
                        {isAdded ? 'Remove' : 'Add'}
                      </Button>
                    </Box>
                  );
                })}
              </Box>
            )}

            {activeAccountTab === 1 && (
              <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
                {(() => {
                  const remainingInactiveEmails = inactiveEmails.filter(
                    email => !invitedEmails.find(inv => inv.email === email && inv.status === 'success')
                  );
                  return remainingInactiveEmails.length === 0 && invitedEmails.length === 0;
                })() ? (
                  <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Typography sx={{ color: '#6B7280', fontSize: '14px' }}>
                      No inactive emails found
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {/* Invited emails with status */}
                    {invitedEmails.map((item, index) => (
                      <Box
                        key={`invited-${index}`}
                        sx={{
                          py: 2,
                          borderBottom: '1px solid #E5E7EB',
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: item.status === 'pending' ? 2 : 0 }}>
                          <Box>
                            <Typography sx={{ fontSize: '14px', color: '#000', fontWeight: 500 }}>
                              {item.email}
                            </Typography>
                            {item.accountType && (
                              <Chip
                                label={item.accountType}
                                size="small"
                                sx={{
                                  backgroundColor: `${getTypeColor(item.accountType)}20`,
                                  color: getTypeColor(item.accountType),
                                  fontWeight: 500,
                                  fontSize: '11px',
                                  height: '20px',
                                  mt: 0.5
                                }}
                              />
                            )}
                          </Box>
                          <Chip
                            label={item.status === 'success' ? 'Invited' : 'Pending'}
                            size="small"
                            sx={{
                              backgroundColor: item.status === 'success' ? '#ECFDF3' : '#FEF3C7',
                              color: item.status === 'success' ? '#15803D' : '#D97706',
                              fontWeight: 500,
                              fontSize: '12px'
                            }}
                          />
                        </Box>
                        {item.status === 'pending' && (
                          <LinearProgress
                            sx={{
                              height: 6,
                              borderRadius: '4px',
                              backgroundColor: '#FEF3C7',
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: '#F59E0B',
                                borderRadius: 2
                              }
                            }}
                          />
                        )}
                      </Box>
                    ))}
                    
                    {/* Inactive emails that haven't been invited yet */}
                    {inactiveEmails
                      .filter(email => !invitedEmails.find(inv => inv.email === email && inv.status === 'success'))
                      .map((email, index) => (
                        <Box
                          key={`inactive-${index}`}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            py: 2,
                            borderBottom: '1px solid #E5E7EB',
                            gap: 2
                          }}
                        >
                          <Typography sx={{ fontSize: '14px', color: '#000', minWidth: '200px' }}>
                            {email}
                          </Typography>
                          <FormControl size="small" sx={{ minWidth: 180 }}>
                            <Select
                              value={selectedAccountTypes[email] || ''}
                              onChange={(e) => handleAccountTypeChange(email, e.target.value)}
                              displayEmpty
                              sx={{
                                borderRadius: '4px',
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#D1D5DB'
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#9CA3AF'
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#2563EB'
                                }
                              }}
                            >
                              <MenuItem value="" disabled>
                                Account type
                              </MenuItem>
                              <MenuItem value="Individual">Individual</MenuItem>
                              <MenuItem value="Corporate">Corporate</MenuItem>
                              <MenuItem value="Trust">Trust</MenuItem>
                            </Select>
                          </FormControl>
                          <Button
                            variant="outlined"
                            disabled={!selectedAccountTypes[email] || invitingEmails.has(email)}
                            onClick={() => handleInviteEmail(email)}
                            sx={{
                              textTransform: 'none',
                              borderColor: selectedAccountTypes[email] && !invitingEmails.has(email) ? '#2563EB' : '#D1D5DB',
                              color: selectedAccountTypes[email] && !invitingEmails.has(email) ? '#2563EB' : '#9CA3AF',
                              px: 3,
                              py: 0.75,
                              borderRadius: '4px',
                              fontWeight: 600,
                              minWidth: '100px',
                              '&:hover': {
                                backgroundColor: selectedAccountTypes[email] && !invitingEmails.has(email) ? '#EFF6FF' : 'transparent',
                                borderColor: selectedAccountTypes[email] && !invitingEmails.has(email) ? '#2563EB' : '#D1D5DB'
                              },
                              '&.Mui-disabled': {
                                borderColor: '#D1D5DB',
                                color: '#9CA3AF'
                              }
                            }}
                          >
                            {invitingEmails.has(email) ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CircularProgress size={14} sx={{ color: '#9CA3AF' }} />
                                <span>Inviting...</span>
                              </Box>
                            ) : (
                              'Invite'
                            )}
                          </Button>
                        </Box>
                      ))}
                  </>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* Step 4: Set Member Permissions */}
        {activeStep === 3 && (
          <Box sx={{ maxHeight: '550px', overflowY: 'auto' }}>
            {selectedAccounts.map((account, index) => (
              <Box
                key={account.id}
                sx={{
                  py: 2,
                  px: 2,
                  borderBottom: index < selectedAccounts.length - 1 ? '1px solid #E5E7EB' : 'none'
                }}
              >
                {/* Account Info and Permission Button - Same Row */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                  {/* Left Side: Account Info */}
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: '15px', color: '#000' }}>
                        {account.name}
                      </Typography>
                      <Chip
                        label={account.type}
                        size="small"
                        sx={{
                          backgroundColor: `${getTypeColor(account.type)}20`,
                          color: getTypeColor(account.type),
                          fontWeight: 500,
                          fontSize: '11px',
                          height: '20px'
                        }}
                      />
                    </Box>
                    <Typography sx={{ fontSize: '13px', color: '#6B7280' }}>
                      {account.email}
                    </Typography>
                  </Box>

                  {/* Right Side: Permission Button */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
                    {accountPermissions[account.id] ? (
                      <>
                        <Button
                          variant="outlined"
                          onClick={() => handleOpenPermissionModal(account)}
                          sx={{
                            textTransform: 'none',
                            borderColor: '#2563EB',
                            color: '#2563EB',
                            px: 3,
                            py: 0.75,
                            borderRadius: '4px',
                            fontWeight: 600,
                            minWidth: '180px',
                            '&:hover': {
                              backgroundColor: '#EFF6FF',
                              borderColor: '#2563EB'
                            }
                          }}
                        >
                          {accountPermissions[account.id].label}
                        </Button>
                        <IconButton
                          onClick={() => handleOpenPermissionModal(account)}
                          sx={{
                            color: '#2563EB',
                            '&:hover': {
                              backgroundColor: '#EFF6FF'
                            }
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </>
                    ) : (
                      <Button
                        variant="outlined"
                        onClick={() => handleOpenPermissionModal(account)}
                        sx={{
                          textTransform: 'none',
                          borderColor: '#2563EB',
                          color: '#2563EB',
                          backgroundColor: '#fff',
                          px: 3,
                          py: 0.75,
                          borderRadius: '4px',
                          fontWeight: 500,
                          minWidth: '180px',
                          '&:hover': {
                            borderColor: '#1D4ED8',
                            backgroundColor: '#EFF6FF'
                          }
                        }}
                      >
                        Select Permission Type
                      </Button>
                    )}
                  </Box>
                </Box>

                {/* Owner Radio Button - Minimal spacing */}
                <FormControlLabel
                  control={
                    <Radio
                      checked={ownerAccount === account.id}
                      onChange={() => handleOwnerChange(account.id)}
                      sx={{
                        color: '#D1D5DB',
                        padding: '1px 1px',
                        '&.Mui-checked': {
                          color: '#2563EB'
                        }
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: '14px', color: '#6B7280' }}>
                      Mark as Owner
                    </Typography>
                  }
                  sx={{ 
                    m: 0,
                    ml: -0.5
                  }}
                />
              </Box>
            ))}
            {selectedAccounts.length > 0 && !hasAllAccountPermissions && (
              <Typography
                sx={{ mt: 2, fontSize: '13px', color: '#DC2626' }}
              >
                Please select a permission type for every household member.
              </Typography>
            )}
            {selectedAccounts.length > 0 && !hasOwnerSelected && (
              <Typography
                sx={{ mt: 0.5, fontSize: '13px', color: '#DC2626' }}
              >
                Please mark at least one household member as the owner.
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>

      {/* Dialog Actions */}
      <DialogActions sx={{ px: 4, py: 3, gap: 2 }}>
        <Button
          onClick={handleBack}
          variant="outlined"
          sx={{
            textTransform: 'none',
            px: 4,
            py: 1.25,
            borderRadius: '4px',
            borderColor: '#D1D5DB',
            color: '#374151',
            fontWeight: 600,
            '&:hover': {
              borderColor: '#9CA3AF',
              backgroundColor: '#F9FAFB'
            }
          }}
        >
          Back
        </Button>
        <Box sx={{ flex: 1 }} />
        {activeStep === 0 && (
          <Button
            onClick={handleNext}
            variant="contained"
            disabled={!householdName.trim()}
            sx={{
              textTransform: 'none',
              px: 4,
              py: 1.25,
              borderRadius: '4px',
              backgroundColor: householdName.trim() ? '#2563EB' : '#E5E7EB',
              color: householdName.trim() ? '#fff' : '#9CA3AF',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: householdName.trim() ? '#1D4ED8' : '#E5E7EB'
              },
              '&.Mui-disabled': {
                backgroundColor: '#E5E7EB',
                color: '#9CA3AF'
              }
            }}
          >
            Next
          </Button>
        )}
        {activeStep === 1 && (
          <Button
            onClick={handleSearchAccounts}
            variant="contained"
            disabled={emails.length === 0 || isSearchingAccounts}
            sx={{
              textTransform: 'none',
              px: 4,
              py: 1.25,
              borderRadius: '4px',
              backgroundColor:
                emails.length > 0 && !isSearchingAccounts ? '#2563EB' : '#E5E7EB',
              color: emails.length > 0 && !isSearchingAccounts ? '#fff' : '#9CA3AF',
              fontWeight: 600,
              '&:hover': {
                backgroundColor:
                  emails.length > 0 && !isSearchingAccounts ? '#1D4ED8' : '#E5E7EB'
              },
              '&.Mui-disabled': {
                backgroundColor: '#E5E7EB',
                color: '#9CA3AF'
              }
            }}
          >
            {isSearchingAccounts ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={18} sx={{ color: '#1D4ED8' }} />
                <span>Searching...</span>
              </Box>
            ) : (
              'Search Accounts'
            )}
          </Button>
        )}
        {activeStep === 2 && (
          <Button
            onClick={handleNext}
            variant="contained"
            disabled={selectedAccounts.length === 0}
            sx={{
              textTransform: 'none',
              px: 4,
              py: 1.25,
              borderRadius: '4px',
              backgroundColor: selectedAccounts.length > 0 ? '#2563EB' : '#E5E7EB',
              color: selectedAccounts.length > 0 ? '#fff' : '#9CA3AF',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: selectedAccounts.length > 0 ? '#1D4ED8' : '#E5E7EB'
              },
              '&.Mui-disabled': {
                backgroundColor: '#E5E7EB',
                color: '#9CA3AF'
              }
            }}
          >
            Next
          </Button>
        )}
        {activeStep === 3 && (
          <Button
            onClick={handleCreateHousehold}
            variant="contained"
            disabled={!canCreateHousehold || isCreatingHousehold}
            sx={{
              textTransform: 'none',
              px: 4,
              py: 1.25,
              borderRadius: '4px',
              backgroundColor:
                canCreateHousehold && !isCreatingHousehold ? '#2563EB' : '#E5E7EB',
              color: canCreateHousehold && !isCreatingHousehold ? '#fff' : '#9CA3AF',
              fontWeight: 600,
              '&:hover': {
                backgroundColor:
                  canCreateHousehold && !isCreatingHousehold ? '#1D4ED8' : '#E5E7EB'
              },
              '&.Mui-disabled': {
                backgroundColor: '#E5E7EB',
                color: '#9CA3AF'
              }
            }}
          >
            {isCreatingHousehold ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={18} sx={{ color: '#1D4ED8' }} />
                <span>Creating...</span>
              </Box>
            ) : (
              'Create Household'
            )}
          </Button>
        )}
      </DialogActions>

      {/* Permission Template Modal */}
      <Dialog
        open={permissionModalOpen}
        onClose={handleClosePermissionModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            width: '550px'
          }
        }}
      >
        <DialogTitle sx={{ pb: 2, pt: 3, px: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
                Select Permission Template
              </Typography>
              <Typography variant="body2" sx={{ color: '#6B7280' }}>
                Following accounts will be linked together in a household
              </Typography>
            </Box>
            <IconButton onClick={handleClosePermissionModal} sx={{ color: '#6B7280', mt: -1 }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ px: 3, py: 3 }}>
          {/* Template Options */}
          <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
            {permissionGroupsError && (
              <Typography
                sx={{
                  position: 'absolute',
                  top: 12,
                  left: 24,
                  fontSize: '12px',
                  color: '#DC2626'
                }}
              >
                {permissionGroupsError}
              </Typography>
            )}
            {/* View-Only Template */}
            <Box
              onClick={() => handleTemplateChange('view-only')}
              sx={{
                flex: 1,
                border: selectedTemplate === 'view-only' ? '2px solid #2563EB' : '2px solid #E5E7EB',
                borderRadius: 3,
                p: 2.5,
                cursor:
                  !isLoadingPermissionGroups && !investorPermissionGroups.viewOnly
                    ? 'not-allowed'
                    : 'pointer',
                position: 'relative',
                transition: 'all 0.2s',
                opacity:
                  !isLoadingPermissionGroups && !investorPermissionGroups.viewOnly ? 0.5 : 1,
                pointerEvents:
                  !isLoadingPermissionGroups && !investorPermissionGroups.viewOnly
                    ? 'none'
                    : 'auto',
                '&:hover': {
                  borderColor: '#2563EB',
                  backgroundColor: '#F9FAFB'
                }
              }}
            >
              <Radio
                checked={selectedTemplate === 'view-only'}
                disabled={!investorPermissionGroups.viewOnly && !isLoadingPermissionGroups}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  padding: 0,
                  color: '#D1D5DB',
                  '&.Mui-checked': {
                    color: '#2563EB'
                  }
                }}
              />
              <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#000', textAlign: 'center', mt: 1 }}>
                View-Only
                {investorPermissionGroups.viewOnly?.groupName
                  ? ` (${investorPermissionGroups.viewOnly.groupName})`
                  : ''}
              </Typography>
            </Box>

            {/* View & Trade Template */}
            <Box
              onClick={() => handleTemplateChange('view-trade')}
              sx={{
                flex: 1,
                border: selectedTemplate === 'view-trade' ? '2px solid #2563EB' : '2px solid #E5E7EB',
                borderRadius: 3,
                p: 2.5,
                cursor:
                  !isLoadingPermissionGroups && !investorPermissionGroups.viewTrade
                    ? 'not-allowed'
                    : 'pointer',
                position: 'relative',
                transition: 'all 0.2s',
                opacity:
                  !isLoadingPermissionGroups && !investorPermissionGroups.viewTrade ? 0.5 : 1,
                pointerEvents:
                  !isLoadingPermissionGroups && !investorPermissionGroups.viewTrade
                    ? 'none'
                    : 'auto',
                '&:hover': {
                  borderColor: '#2563EB',
                  backgroundColor: '#F9FAFB'
                }
              }}
            >
              <Radio
                checked={selectedTemplate === 'view-trade'}
                disabled={!investorPermissionGroups.viewTrade && !isLoadingPermissionGroups}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  padding: 0,
                  color: '#D1D5DB',
                  '&.Mui-checked': {
                    color: '#2563EB'
                  }
                }}
              />
              <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#000', textAlign: 'center', mt: 1 }}>
                View & Trade
                {investorPermissionGroups.viewTrade?.groupName
                  ? ` (${investorPermissionGroups.viewTrade.groupName})`
                  : ''}
              </Typography>
            </Box>

            {/* Custom Template */}
            {/* <Box
              onClick={() => handleTemplateChange('custom')}
              sx={{
                flex: 1,
                border: selectedTemplate === 'custom' ? '2px solid #2563EB' : '2px solid #E5E7EB',
                borderRadius: 3,
                p: 2.5,
                cursor: 'pointer',
                position: 'relative',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: '#2563EB',
                  backgroundColor: '#F9FAFB'
                }
              }}
            >
              <Radio
                checked={selectedTemplate === 'custom'}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  padding: 0,
                  color: '#D1D5DB',
                  '&.Mui-checked': {
                    color: '#2563EB'
                  }
                }}
              />
              <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#000', textAlign: 'center', mt: 1 }}>
                Custom
              </Typography>
            </Box> */}
          </Box>

          {/* Permissions / Group Details */}
          {isLoadingPermissionGroups && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 1
              }}
            >
              <CircularProgress size={18} sx={{ color: '#2563EB' }} />
              <Typography sx={{ fontSize: '13px', color: '#6B7280' }}>
                Loading permission templates...
              </Typography>
            </Box>
          )}

          {selectedTemplate === 'view-only' && investorPermissionGroups.viewOnly && (
            <Box
              sx={{
                mt: 1,
                borderRadius: 3,
                border: '1px solid #E5E7EB',
                px: 3,
                py: 2
              }}
            >
              <Typography
                sx={{ fontWeight: 600, fontSize: '14px', color: '#111827', mb: 1 }}
              >
                {investorPermissionGroups.viewOnly.groupName}
              </Typography>
              <List dense sx={{ maxHeight: 260, overflow: 'auto' }}>
                {(investorPermissionGroups.viewOnly.apiNames || []).map((api) => (
                  <ListItem key={api} sx={{ py: 0.5 }}>
                    <CheckCircleIcon
                      sx={{ color: '#16A34A', fontSize: 20, mr: 1.5 }}
                    />
                    <ListItemText
                      primary={api}
                      primaryTypographyProps={{
                        fontSize: '14px',
                        color: '#111827'
                      }}
                    />
                  </ListItem>
                ))}
                {(!investorPermissionGroups.viewOnly.apiNames ||
                  investorPermissionGroups.viewOnly.apiNames.length === 0) && (
                  <ListItem>
                    <CheckCircleIcon
                      sx={{ color: '#9CA3AF', fontSize: 20, mr: 1.5 }}
                    />
                    <ListItemText
                      primary="No APIs configured for this group."
                      primaryTypographyProps={{
                        fontSize: '13px',
                        color: '#6B7280'
                      }}
                    />
                  </ListItem>
                )}
              </List>
            </Box>
          )}

          {selectedTemplate === 'view-trade' && investorPermissionGroups.viewTrade && (
            <Box
              sx={{
                mt: 1,
                borderRadius: 3,
                border: '1px solid #E5E7EB',
                px: 3,
                py: 2
              }}
            >
              <Typography
                sx={{ fontWeight: 600, fontSize: '14px', color: '#111827', mb: 1 }}
              >
                {investorPermissionGroups.viewTrade.groupName}
              </Typography>
              <List dense sx={{ maxHeight: 260, overflow: 'auto' }}>
                {(investorPermissionGroups.viewTrade.apiNames || []).map((api) => (
                  <ListItem key={api} sx={{ py: 0.5 }}>
                    <CheckCircleIcon
                      sx={{ color: '#16A34A', fontSize: 20, mr: 1.5 }}
                    />
                    <ListItemText
                      primary={api}
                      primaryTypographyProps={{
                        fontSize: '14px',
                        color: '#111827'
                      }}
                    />
                  </ListItem>
                ))}
                {(!investorPermissionGroups.viewTrade.apiNames ||
                  investorPermissionGroups.viewTrade.apiNames.length === 0) && (
                  <ListItem>
                    <CheckCircleIcon
                      sx={{ color: '#9CA3AF', fontSize: 20, mr: 1.5 }}
                    />
                    <ListItemText
                      primary="No APIs configured for this group."
                      primaryTypographyProps={{
                        fontSize: '13px',
                        color: '#6B7280'
                      }}
                    />
                  </ListItem>
                )}
              </List>
            </Box>
          )}

          {/* {selectedTemplate === 'custom' && (
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={customPermissions.createUser}
                    onChange={() => handleCustomPermissionToggle('createUser')}
                    sx={{
                      color: '#D1D5DB',
                      '&.Mui-checked': {
                        color: '#10B981'
                      }
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: '14px', color: '#374151' }}>
                    Create User
                  </Typography>
                }
                sx={{ mb: 1.5, width: '100%' }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={customPermissions.updateUserInfo}
                    onChange={() => handleCustomPermissionToggle('updateUserInfo')}
                    sx={{
                      color: '#D1D5DB',
                      '&.Mui-checked': {
                        color: '#10B981'
                      }
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: '14px', color: '#374151' }}>
                    Update User Info
                  </Typography>
                }
                sx={{ mb: 1.5, width: '100%' }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={customPermissions.viewReports}
                    onChange={() => handleCustomPermissionToggle('viewReports')}
                    sx={{
                      color: '#D1D5DB',
                      '&.Mui-checked': {
                        color: '#10B981'
                      }
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: '14px', color: '#374151' }}>
                    View Reports
                  </Typography>
                }
                sx={{ mb: 1.5, width: '100%' }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={customPermissions.manageTransactions}
                    onChange={() => handleCustomPermissionToggle('manageTransactions')}
                    sx={{
                      color: '#D1D5DB',
                      '&.Mui-checked': {
                        color: '#10B981'
                      }
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: '14px', color: '#374151' }}>
                    Manage Transactions
                  </Typography>
                }
                sx={{ mb: 1.5, width: '100%' }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={customPermissions.editSettings}
                    onChange={() => handleCustomPermissionToggle('editSettings')}
                    sx={{
                      color: '#D1D5DB',
                      '&.Mui-checked': {
                        color: '#10B981'
                      }
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: '14px', color: '#374151' }}>
                    Edit Settings
                  </Typography>
                }
                sx={{ mb: 1.5, width: '100%' }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={customPermissions.deleteUser}
                    onChange={() => handleCustomPermissionToggle('deleteUser')}
                    sx={{
                      color: '#D1D5DB',
                      '&.Mui-checked': {
                        color: '#10B981'
                      }
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: '14px', color: '#374151' }}>
                    Delete User
                  </Typography>
                }
                sx={{ mb: 1.5, width: '100%' }}
              />
            </Box>
          )} */}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 3, gap: 2 }}>
          <Button
            onClick={handleClosePermissionModal}
            variant="outlined"
            sx={{
              textTransform: 'none',
              px: 4,
              py: 1.25,
              borderRadius: '4px',
              borderColor: '#D1D5DB',
              color: '#374151',
              fontWeight: 600,
              '&:hover': {
                borderColor: '#9CA3AF',
                backgroundColor: '#F9FAFB'
              }
            }}
          >
            Cancel
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            onClick={handleContinuePermission}
            variant="contained"
            disabled={!selectedTemplate}
            sx={{
              textTransform: 'none',
              px: 4,
              py: 1.25,
              borderRadius: '4px',
              backgroundColor: selectedTemplate ? '#2563EB' : '#E5E7EB',
              color: selectedTemplate ? '#fff' : '#9CA3AF',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: selectedTemplate ? '#1D4ED8' : '#E5E7EB'
              },
              '&.Mui-disabled': {
                backgroundColor: '#E5E7EB',
                color: '#9CA3AF'
              }
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default CreateHouseholdModal;

